import PropTypes from "prop-types";
import React from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  FormGroup,
  FormLabel,
  FormText,
} from "react-bootstrap";
import Select from "react-select";
import { getTargets } from "redux-store/devices";
import { BackButton } from "ui/button/back";
import { Notification } from "ui/notification";
import { Table, Column } from "ui/table";
import { DeviceStatusIcon } from "ui/table/format/device-status-icon";

import { UpgradeConfig } from "./device-config";
import {
  formatNetworkLink,
  formatPrimaryLink,
  sortStatusIcon,
} from "./table-format";

class DeviceSelector extends React.PureComponent {
  static get propTypes() {
    return {
      selectedOperation: PropTypes.number,
      selectedVersion: PropTypes.string,
      devices: PropTypes.object.isRequired,
      deviceStatusOnline: PropTypes.bool,
      onResetComplete: PropTypes.func,
      resetForm: PropTypes.bool,
      submit: PropTypes.func,
      submitted: PropTypes.bool,
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      ...this._getInitialState(),
    };

    this._formatStatus = this._formatStatus.bind(this);
    this._onSelect = this._onSelect.bind(this);
    this._showModal = this._showModal.bind(this);

    this.selectHelpText = (olderNewer) => `
      Required. Please select the target devices
      you want to perform this operation on. You can select individual
      devices as well as tags. Only devices using ${olderNewer} version will appear.`;
  }

  componentDidUpdate(prevProps) {
    if (
      (this.props.resetForm && !prevProps.resetForm) ||
      prevProps.selectedOperation !== this.props.selectedOperation ||
      prevProps.selectedVersion !== this.props.selectedVersion
    ) {
      this.setState(this._getInitialState(), () => {
        if (this.props.resetForm && this.props.onResetComplete) {
          this.props.onResetComplete();
        }
      });
    }
  }

  _getInitialState() {
    return {
      errorMessage: "",
      selectedOpts: [],
      showModal: false,
    };
  }

  _onSelect(selectedTags) {
    this.setState({
      selectedOpts: selectedTags,
    });
  }

  _getTableData() {
    const { selectedOpts } = this.state;
    const data = [];
    const selected = new Set(selectedOpts.map((v) => v.value));

    Object.values(this.props.devices).forEach((device) => {
      if (selected.has(`d_${device.id}`)) {
        data.push(device);
        // Don't stop iterating over devices.
        return true;
      }

      device.tags.some((tag) => {
        if (selected.has(`t_${tag.id}`)) {
          data.push(device);
          // Stop iterating over tags.
          return true;
        }

        return false;
      });
    });

    return data;
  }

  _showModal() {
    const tableData = this._getTableData();
    let error = "";

    if (!tableData || tableData.length === 0) {
      error = "Please select at least one device.";
    }

    this.setState({
      showModal: error === "",
      errorMessage: error,
    });
  }

  _formatStatus(status, device) {
    const { deviceStatusOnline } = this.props;

    return (
      <DeviceStatusIcon
        device={device}
        deviceStatusOnline={deviceStatusOnline}
      />
    );
  }

  _renderModal() {
    if (!this.state.showModal) {
      return;
    }

    const hide = () => {
      this.setState({ showModal: false });
    };

    const submit = () => {
      this.setState({ showModal: false });
      this.props.submit(this._getTableData());
    };

    const { selectedOperation } = this.props;
    const buttonText = UpgradeConfig[selectedOperation].submitButtonLabel;
    const popupWarning = UpgradeConfig[selectedOperation].popupWarning;

    return (
      <Modal className="device-confirm-modal" show={true} onHide={hide}>
        <Modal.Header>
          <Modal.Title>{popupWarning}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{this._renderSelectedDevicesTable()}</Modal.Body>
        <Modal.Footer>
          <Button onClick={hide} variant="secondary">
            Close
          </Button>
          <Button onClick={submit} variant="danger">
            {buttonText}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  _modifyDevicesForDowngrade(targetVersion) {
    const { devices } = this.props;

    const updatedDevices = Object.entries(devices).reduce(
      (acc, [key, device]) => {
        acc[key] = {
          ...device,
          downgrade_target_version:
            device.downgrade_target_version || targetVersion,
        };
        return acc;
      },
      {},
    );

    this.setState({ devices: updatedDevices });

    return updatedDevices;
  }

  _renderSelectedDevicesTable() {
    const { selectedOperation } = this.props;

    const tableData = this._getTableData();
    const currentVersionLabel =
      UpgradeConfig[selectedOperation].currentVersionLabel;
    const currentVersionField =
      UpgradeConfig[selectedOperation].currentVersionField;
    const targetVersionLabel =
      UpgradeConfig[selectedOperation].targetVersionLabel;
    const targetVersionField =
      UpgradeConfig[selectedOperation].targetVersionField;

    return (
      <Table data={tableData} hover={true} pagination={true}>
        <Column
          key="status"
          dataField="status"
          dataSort={true}
          width="80px"
          dataAlign="center"
          dataFormat={this._formatStatus}
          sortFunc={sortStatusIcon}
        >
          Status
        </Column>
        <Column
          key="device_id"
          dataField="device_id"
          dataSort={true}
          width="300px"
          isKey={true}
          dataFormat={formatPrimaryLink}
        >
          ID
        </Column>
        <Column key="name" dataField="name" dataSort={true}>
          Name
        </Column>
        <Column key="version" dataSort={true} dataField={currentVersionField}>
          {currentVersionLabel}
        </Column>
        <Column key="lversion" dataSort={true} dataField={targetVersionField}>
          {targetVersionLabel}
        </Column>
        <Column
          key="network"
          dataField="network_name"
          dataSort={true}
          dataFormat={formatNetworkLink}
        >
          Network
        </Column>
      </Table>
    );
  }

  render() {
    const { selectedOperation, devices, submitted } = this.props;
    const { errorMessage, selectedOpts } = this.state;
    const buttonText = UpgradeConfig[selectedOperation].submitButtonLabel;
    const options = getTargets(Object.values(devices), true);
    const deviceHelpText = UpgradeConfig[selectedOperation].deviceHelpText;

    return (
      <Row>
        <Col xs={12}>
          <Notification error={errorMessage} />
          <FormGroup>
            <FormLabel>Select Devices</FormLabel>
            <Select
              name="select-devices"
              onChange={this._onSelect}
              options={options}
              value={selectedOpts}
              isMulti
            />
            <FormText>{deviceHelpText}</FormText>
          </FormGroup>
          {this._renderSelectedDevicesTable()}
          {this._renderModal()}
          <BackButton />
          <Button
            variant="primary"
            name="submit-button"
            onClick={this._showModal}
            disabled={submitted}
          >
            {buttonText}
          </Button>
        </Col>
      </Row>
    );
  }
}

export { DeviceSelector };
